<template>
  <div class="product-pricing-import">
    <!-- Step 1: Download Template -->
    <b-row class="mb-4">
      <b-col>
        <div class="form-group">
          <span>
            <b>Bước 1:</b> Vui lòng tải file mẫu và chuẩn bị dữ liệu đúng format
          </span>
          <br />
          <b-button
            variant="outline-primary"
            size="sm"
            class="mt-2"
            @click="downloadTemplate"
            :disabled="isDownloading"
          >
            <b-spinner small v-if="isDownloading" class="mr-2"></b-spinner>
            <i v-else class="fas fa-download mr-2"></i>
            Tải file mẫu Excel
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Template format info -->
    <b-row class="mb-4">
      <b-col>
        <b-alert show variant="info">
          <h6><i class="fas fa-info-circle"></i> Định dạng file Excel:</h6>
          <ul class="mb-0">
            <li>
              <strong>Tên sản phẩm</strong> (bắt buộc) - Tên chính xác của sản
              phẩm trong hệ thống
            </li>
            <li>
              <strong>Tên chi nhánh</strong> (bắt buộc) - Tên chính xác của chi
              nhánh trong hệ thống
            </li>
            <li>
              <strong>Giá bán lẻ</strong> (tùy chọn) - Giá bán lẻ sản phẩm
            </li>
            <li>
              <strong>Giá bán buôn</strong> (tùy chọn) - Giá bán buôn sản phẩm
            </li>
            <li>
              <strong>Giá niêm yết</strong> (tùy chọn) - Giá niêm yết sản phẩm
            </li>
          </ul>
          <p class="mb-0 mt-2">
            <i class="fas fa-exclamation-triangle text-warning"></i>
            <strong>Lưu ý:</strong> Ít nhất một trong ba loại giá phải được điền
          </p>
        </b-alert>
      </b-col>
    </b-row>

    <!-- Step 2: Upload File -->
    <b-row class="mb-4">
      <b-col cols="12">
        <span><b>Bước 2:</b> Tải lên file Excel</span>

        <!-- Drag and Drop Area -->
        <div
          class="drop-zone mt-3 mb-3"
          :class="{ 'drop-zone-active': isDragging }"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
          v-if="!fileName && !isProcessing"
        >
          <div class="text-center">
            <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <p class="mb-2">Kéo thả file Excel vào đây hoặc</p>
            <b-button variant="primary" @click="$refs.fileInput.click()">
              <i class="fas fa-folder-open mr-2"></i>
              Chọn từ máy tính
            </b-button>
            <p class="text-muted mt-2 mb-0">
              <small>Chấp nhận file .xlsx, .xls (tối đa 2MB)</small>
            </p>
          </div>
        </div>

        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          class="d-none"
          @change="onFileSelected"
        />

        <!-- File info display -->
        <div v-if="fileName && !isProcessing" class="file-info-display">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <i class="fas fa-file-excel text-success mr-2"></i>
              <strong>{{ fileName }}</strong>
              <small class="text-muted ml-2"
                >({{ formatFileSize(fileSize) }})</small
              >
            </div>
            <b-button variant="link" class="text-danger" @click="clearFile">
              <i class="fas fa-times"></i> Xóa
            </b-button>
          </div>
        </div>

        <!-- Processing Status -->
        <div v-if="isProcessing" class="processing-status mt-4">
          <b-alert :variant="processStatus.variant" show>
            <div class="d-flex align-items-center">
              <b-spinner
                v-if="processStatus.loading"
                small
                class="mr-2"
              ></b-spinner>
              <i v-else :class="processStatus.icon" class="mr-2"></i>
              <span>{{ processStatus.message }}</span>
            </div>
          </b-alert>

          <b-progress
            v-if="processStatus.loading"
            :value="processProgress"
            :max="100"
            height="25px"
            animated
            striped
          >
            <b-progress-bar :value="processProgress">
              <span>{{ processProgress }}%</span>
            </b-progress-bar>
          </b-progress>
        </div>
      </b-col>
    </b-row>

    <!-- Step 3: Import Button -->
    <b-row v-if="fileName && !isProcessing">
      <b-col>
        <span><b>Bước 3:</b> Bắt đầu import</span>
        <br />
        <b-button
          variant="primary"
          class="mt-2"
          @click="startImport"
          :disabled="isImporting || !selectedFile"
        >
          <b-spinner small v-if="isImporting" class="mr-2"></b-spinner>
          <i v-else class="fas fa-upload mr-2"></i>
          Bắt đầu Import
        </b-button>
      </b-col>
    </b-row>

    <!-- Import Result -->
    <b-row v-if="importResult" class="mt-4">
      <b-col>
        <b-alert show variant="success">
          <i class="fas fa-check-circle mr-2"></i>
          <strong>{{ importResult.message }}</strong>
        </b-alert>
        <b-button variant="primary" @click="resetImport">
          <i class="fas fa-redo mr-2"></i>
          Import file khác
        </b-button>
      </b-col>
    </b-row>

    <!-- Import Error -->
    <b-row v-if="importError" class="mt-4">
      <b-col>
        <b-alert show variant="danger">
          <i class="fas fa-exclamation-circle mr-2"></i>
          <strong>Import thất bại:</strong> {{ importError }}
        </b-alert>
        <b-button variant="secondary" @click="clearFile">
          <i class="fas fa-redo mr-2"></i>
          Thử lại
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ProductStorePricingService } from '@/view/pages/products/services/ProductStorePricingService';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

export default {
  name: 'ProductPricingImportModal',
  data() {
    return {
      // File handling
      fileName: '',
      fileSize: 0,
      selectedFile: null,
      isDragging: false,
      isDownloading: false,

      // Import process
      isImporting: false,
      isProcessing: false,
      processProgress: 0,

      // Status
      processStatus: {
        message: '',
        variant: 'info',
        loading: false,
        icon: 'fas fa-info-circle',
      },
      importResult: null,
      importError: null,
    };
  },
  methods: {
    async downloadTemplate() {
      this.isDownloading = true;
      try {
        const response = await ProductStorePricingService.downloadTemplate();

        // Create blob and download
        const blob = new Blob([response.data], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'product-pricing-template.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        makeToastSuccess('Tải file mẫu thành công');
      } catch (error) {
        console.error('Error downloading template:', error);
        makeToastFaile('Không thể tải file mẫu');
      } finally {
        this.isDownloading = false;
      }
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    onFileSelected(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFile(file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
      if (!validTypes.includes(file.type)) {
        makeToastFaile('Chỉ chấp nhận file Excel (.xlsx, .xls)');
        return;
      }

      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        makeToastFaile('Kích thước file không được vượt quá 2MB');
        return;
      }

      this.selectedFile = file;
      this.fileName = file.name;
      this.fileSize = file.size;
    },

    clearFile() {
      this.selectedFile = null;
      this.fileName = '';
      this.fileSize = 0;
      this.importError = null;
      this.importResult = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async startImport() {
      if (!this.selectedFile) {
        makeToastFaile('Vui lòng chọn file để import');
        return;
      }

      this.isImporting = true;
      this.isProcessing = true;
      this.importError = null;
      this.importResult = null;

      // Start progress
      this.processProgress = 0;
      this.processStatus = {
        message: 'Đang tải file lên...',
        variant: 'info',
        loading: true,
        icon: 'fas fa-spinner fa-spin',
      };

      const progressInterval = setInterval(() => {
        if (this.processProgress < 90) {
          this.processProgress += 10;
        }
      }, 200);

      try {
        this.processStatus.message = 'Đang xử lý dữ liệu...';

        const result = await ProductStorePricingService.importPricing(
          this.selectedFile,
        );

        clearInterval(progressInterval);
        this.processProgress = 100;

        this.processStatus = {
          message: 'Import hoàn tất!',
          variant: 'success',
          loading: false,
          icon: 'fas fa-check-circle',
        };

        setTimeout(() => {
          this.importResult = result;
          this.isProcessing = false;
          makeToastSuccess(result.message || 'Import thành công');
        }, 500);
      } catch (error) {
        clearInterval(progressInterval);
        this.processProgress = 0;

        const errorMessage =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          'Đã xảy ra lỗi khi import';

        this.processStatus = {
          message: 'Import thất bại!',
          variant: 'danger',
          loading: false,
          icon: 'fas fa-exclamation-circle',
        };

        setTimeout(() => {
          this.importError = errorMessage;
          this.isProcessing = false;
          makeToastFaile(errorMessage);
        }, 500);
      } finally {
        this.isImporting = false;
      }
    },

    resetImport() {
      this.clearFile();
      this.isProcessing = false;
      this.processProgress = 0;
      this.processStatus = {
        message: '',
        variant: 'info',
        loading: false,
        icon: 'fas fa-info-circle',
      };
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },
  },
};
</script>

<style lang="scss" scoped>
.product-pricing-import {
  .drop-zone {
    border: 2px dashed #d1d3e2;
    border-radius: 8px;
    padding: 40px;
    transition: all 0.3s ease;
    background-color: #f8f9fc;

    &:hover {
      border-color: #4e73df;
      background-color: #eaecf4;
    }

    &.drop-zone-active {
      border-color: #4e73df;
      background-color: #d1d3e2;
      transform: scale(1.02);
    }
  }

  .file-info-display {
    padding: 15px;
    border: 1px solid #e3e6f0;
    border-radius: 8px;
    background-color: #f8f9fc;
  }

  .processing-status {
    .alert {
      margin-bottom: 15px;
    }
  }
}
</style>
